﻿import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import GetAppIcon from "@material-ui/icons/GetApp";
import FilterListIcon from "@material-ui/icons/FilterList";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import AssignmentTurnedInOutlinedIcon from "@material-ui/icons/AssignmentTurnedInOutlined";
import ScheduleOutlinedIcon from "@material-ui/icons/ScheduleOutlined";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import HistoryOutlinedIcon from "@material-ui/icons/HistoryOutlined";
import PersonOutlineOutlinedIcon from "@material-ui/icons/PersonOutlineOutlined";
import CalendarTodayOutlinedIcon from "@material-ui/icons/CalendarTodayOutlined";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  pagePaper: {
    flex: 1,
    padding: theme.spacing(2.5),
    overflowY: "auto",
    background: theme.palette.background.paper,
    ...theme.scrollbarStyles
  },
  introRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  introText: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5)
  },
  pageTitle: {
    fontWeight: 800
  },
  pageSubtitle: {
    color: theme.palette.text.secondary,
    maxWidth: 760
  },
  tabs: {
    marginBottom: theme.spacing(2)
  },
  actionRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap"
  },
  historyActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  actionButton: {
    borderRadius: 999,
    minHeight: 42,
    paddingInline: theme.spacing(2),
    fontWeight: 700
  },
  metricsGrid: {
    marginBottom: theme.spacing(2)
  },
  metricCard: {
    padding: theme.spacing(2.25),
    borderRadius: 18,
    minHeight: 112,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(246,250,249,0.98) 100%)",
    border: "1px solid rgba(18, 140, 126, 0.10)"
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    fontWeight: 700
  },
  metricValue: {
    fontSize: "1.9rem",
    fontWeight: 800,
    lineHeight: 1
  },
  iconMuted: {
    color: theme.palette.text.secondary,
    opacity: 0.8
  },
  tablePaper: {
    borderRadius: 20,
    overflow: "hidden",
    border: "1px solid rgba(18, 140, 126, 0.10)"
  },
  tableHeaderCell: {
    fontWeight: 800,
    color: "#334155",
    backgroundColor: "rgba(248, 250, 252, 0.96)"
  },
  rowName: {
    fontWeight: 700,
    color: "#0f172a"
  },
  rowMeta: {
    marginTop: theme.spacing(0.4),
    color: theme.palette.text.secondary,
    fontSize: "0.86rem"
  },
  protocolButton: {
    justifyContent: "flex-start",
    textTransform: "none",
    fontWeight: 700
  },
  drawerPaper: {
    width: 360,
    maxWidth: "92vw",
    padding: theme.spacing(2.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
  },
  drawerFooter: {
    marginTop: "auto",
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },
  filterField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 14
    }
  },
  filterSummary: {
    color: theme.palette.text.secondary
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary
  },
  actionsSection: {
    padding: theme.spacing(2),
    borderRadius: 18,
    marginBottom: theme.spacing(2)
  },
  actionsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5)
  },
  actionsTitle: {
    fontWeight: 700
  },
  actionsDescription: {
    color: theme.palette.text.secondary
  },
  actionsRow: {
    display: "flex",
    gap: theme.spacing(1.25),
    flexWrap: "wrap"
  },
  connectionCard: {
    padding: theme.spacing(2),
    borderRadius: 16,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
    height: "100%"
  },
  connectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1.5)
  },
  connectionTitle: {
    fontWeight: 800
  },
  connectionSubtitle: {
    color: theme.palette.text.secondary
  },
  countersRow: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  counterChip: {
    backgroundColor: "rgba(148, 163, 184, 0.12)",
    color: theme.palette.text.primary
  },
  connectionActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  subtleButton: {
    minWidth: 0
  }
}));

const defaultHistoryFilters = {
  searchParam: "",
  queueIds: [],
  userIds: [],
  whatsappIds: [],
  tagIds: [],
  dateFrom: "",
  dateTo: ""
};

const formatTicketProtocol = ticketId => `#${String(ticketId || "").padStart(6, "0")}`;

const formatDateTime = value => {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleString("pt-BR");
};

const getClosedDisplayDate = ticket => ticket?.closedAt || ticket?.updatedAt;

const getAttendantLabel = (ticket, usersById = new Map()) => {
  if (ticket?.user?.name) {
    return ticket.user.name;
  }

  const userId = Number(ticket?.userId || 0);
  if (userId > 0) {
    const fallbackName = usersById.get(userId);
    if (fallbackName) {
      return fallbackName;
    }

    return `Atendente (ID ${userId})`;
  }

  return "Sem atendente";
};

const escapeHtml = value =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const exportRowsToExcel = (rows, usersById = new Map()) => {
  const htmlRows = rows
    .map(
      row => `
        <tr>
          <td>${escapeHtml(row.contact?.name || "Sem contato")}</td>
          <td>${escapeHtml(row.whatsapp?.name || "Sem conexão")}</td>
          <td>${escapeHtml(row.queue?.name || "Sem fila")}</td>
          <td>${escapeHtml(getAttendantLabel(row, usersById))}</td>
          <td>${escapeHtml(row.subject || "-")}</td>
          <td>${escapeHtml(formatDateTime(row.createdAt))}</td>
          <td>${escapeHtml(formatDateTime(getClosedDisplayDate(row)))}</td>
          <td>${escapeHtml(formatTicketProtocol(row.id))}</td>
        </tr>
      `
    )
    .join("");

  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8" /></head>
      <body>
        <table border="1" cellspacing="0" cellpadding="6">
          <tr>
            <th>Nome</th>
            <th>Conexão</th>
            <th>Departamento</th>
            <th>Atendente</th>
            <th>Assunto</th>
            <th>Início</th>
            <th>Encerrado em</th>
            <th>Protocolo</th>
          </tr>
          ${htmlRows}
        </table>
      </body>
    </html>
  `;

  const blob = new Blob(["\ufeff", html], {
    type: "application/vnd.ms-excel;charset=utf-8;"
  });

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `historico-atendimentos-${new Date()
    .toISOString()
    .slice(0, 10)}.xls`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const Attendances = () => {
  const classes = useStyles();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState("history");
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingManagement, setLoadingManagement] = useState(true);
  const [historyFiltersOpen, setHistoryFiltersOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    users: [],
    queues: [],
    whatsapps: [],
    tags: []
  });
  const [draftHistoryFilters, setDraftHistoryFilters] = useState(
    defaultHistoryFilters
  );
  const [historyFilters, setHistoryFilters] = useState(defaultHistoryFilters);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyData, setHistoryData] = useState({
    tickets: [],
    count: 0
  });
  const [summary, setSummary] = useState({
    totals: { connections: 0, open: 0, pending: 0, closed: 0 },
    items: []
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmInfo, setConfirmInfo] = useState({
    title: "",
    message: "",
    payload: null
  });

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const { data } = await api.get("/tickets", {
        params: {
          status: "closed",
          showAll: "true",
          pageNumber: String(historyPage + 1),
          ...(historyFilters.searchParam
            ? { searchParam: historyFilters.searchParam }
            : {}),
          ...(historyFilters.dateFrom
            ? { dateFrom: historyFilters.dateFrom }
            : {}),
          ...(historyFilters.dateTo ? { dateTo: historyFilters.dateTo } : {}),
          ...(historyFilters.queueIds.length
            ? { queueIds: JSON.stringify(historyFilters.queueIds) }
            : {}),
          ...(historyFilters.userIds.length
            ? { userIds: JSON.stringify(historyFilters.userIds) }
            : {}),
          ...(historyFilters.whatsappIds.length
            ? { whatsappIds: JSON.stringify(historyFilters.whatsappIds) }
            : {}),
          ...(historyFilters.tagIds.length
            ? { tagIds: JSON.stringify(historyFilters.tagIds) }
            : {})
        }
      });

      setHistoryData({
        tickets: Array.isArray(data?.tickets) ? data.tickets : [],
        count: Number(data?.count || 0)
      });
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingHistory(false);
    }
  }, [historyFilters, historyPage]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const { data } = await api.get("/tickets/filter-options");
      setFilterOptions({
        users: Array.isArray(data?.users) ? data.users : [],
        queues: Array.isArray(data?.queues) ? data.queues : [],
        whatsapps: Array.isArray(data?.whatsapps) ? data.whatsapps : [],
        tags: Array.isArray(data?.tags) ? data.tags : []
      });
    } catch (err) {
      toastError(err);
    }
  }, []);

  const fetchManagementSummary = useCallback(async () => {
    setLoadingManagement(true);
    try {
      const { data } = await api.get("/whatsapp/attendance/summary");
      setSummary(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingManagement(false);
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    fetchManagementSummary();
  }, [fetchManagementSummary]);

  const totals = useMemo(() => summary.totals || {}, [summary]);
  const usersById = useMemo(() => {
    const entries = (filterOptions.users || [])
      .map(user => [Number(user.id), String(user.name || "").trim()])
      .filter(([id, name]) => id > 0 && Boolean(name));
    return new Map(entries);
  }, [filterOptions.users]);

  const activeFiltersCount = useMemo(
    () =>
      [
        historyFilters.searchParam,
        historyFilters.dateFrom,
        historyFilters.dateTo,
        historyFilters.queueIds.length,
        historyFilters.userIds.length,
        historyFilters.whatsappIds.length,
        historyFilters.tagIds.length
      ].filter(Boolean).length,
    [historyFilters]
  );

  const handleRequestClose = payload => {
    const scopeLabel = payload.whatsappId
      ? `na conexão ${payload.name}`
      : "em todas as conexões";

    const affected = [
      payload.closeOpen ? '"Atendendo"' : null,
      payload.closePending ? '"Aguardando"' : null
    ]
      .filter(Boolean)
      .join(" e ");

    setConfirmInfo({
      title: "Confirmar fechamento de atendimentos",
      message: `Deseja fechar todos os chamados ${affected} ${scopeLabel}? Essa operação não poderá ser desfeita.`,
      payload
    });
    setConfirmOpen(true);
  };

  const handleConfirmClose = async () => {
    if (!confirmInfo.payload) return;

    try {
      await api.post("/whatsapp/attendance/close", {
        whatsappId: confirmInfo.payload.whatsappId,
        closeOpen: confirmInfo.payload.closeOpen,
        closePending: confirmInfo.payload.closePending
      });
      toast.success("Atendimentos atualizados com sucesso.");
      fetchManagementSummary();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDraftFilterChange = field => event => {
    setDraftHistoryFilters(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleApplyFilters = () => {
    setHistoryPage(0);
    setHistoryFilters(draftHistoryFilters);
    setHistoryFiltersOpen(false);
  };

  const handleClearFilters = () => {
    setHistoryPage(0);
    setDraftHistoryFilters(defaultHistoryFilters);
    setHistoryFilters(defaultHistoryFilters);
  };

  const renderMetricCard = (label, value, icon) => (
    <Paper variant="outlined" className={classes.metricCard}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography className={classes.metricLabel}>{label}</Typography>
        <span className={classes.iconMuted}>{icon}</span>
      </Box>
      <Typography className={classes.metricValue}>{value}</Typography>
    </Paper>
  );

  const openTicket = ticketId => {
    history.push(`/tickets/${ticketId}`);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={confirmInfo.title}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={handleConfirmClose}
      >
        {confirmInfo.message}
      </ConfirmationModal>

      <Drawer
        anchor="right"
        open={historyFiltersOpen}
        onClose={() => setHistoryFiltersOpen(false)}
        classes={{ paper: classes.drawerPaper }}
      >
        <div>
          <Typography variant="h6">Filtros do histórico</Typography>
          <Typography variant="body2" className={classes.filterSummary}>
            Refine os atendimentos encerrados por nome, fila, atendente, conexão,
            tag e período.
          </Typography>
        </div>

        <TextField
          className={classes.filterField}
          variant="outlined"
          label="Buscar"
          placeholder="Nome, número, assunto ou conteúdo"
          value={draftHistoryFilters.searchParam}
          onChange={handleDraftFilterChange("searchParam")}
        />

        <TextField
          className={classes.filterField}
          variant="outlined"
          label="De"
          type="date"
          value={draftHistoryFilters.dateFrom}
          onChange={handleDraftFilterChange("dateFrom")}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          className={classes.filterField}
          variant="outlined"
          label="Até"
          type="date"
          value={draftHistoryFilters.dateTo}
          onChange={handleDraftFilterChange("dateTo")}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          select
          SelectProps={{ multiple: true }}
          className={classes.filterField}
          variant="outlined"
          label="Fila"
          value={draftHistoryFilters.queueIds}
          onChange={handleDraftFilterChange("queueIds")}
        >
          {filterOptions.queues.map(queue => (
            <MenuItem key={queue.id} value={queue.id}>
              {queue.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          SelectProps={{ multiple: true }}
          className={classes.filterField}
          variant="outlined"
          label="Atendente"
          value={draftHistoryFilters.userIds}
          onChange={handleDraftFilterChange("userIds")}
        >
          {filterOptions.users.map(option => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          SelectProps={{ multiple: true }}
          className={classes.filterField}
          variant="outlined"
          label="Conexão"
          value={draftHistoryFilters.whatsappIds}
          onChange={handleDraftFilterChange("whatsappIds")}
        >
          {filterOptions.whatsapps.map(option => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          SelectProps={{ multiple: true }}
          className={classes.filterField}
          variant="outlined"
          label="Tag"
          value={draftHistoryFilters.tagIds}
          onChange={handleDraftFilterChange("tagIds")}
        >
          {filterOptions.tags.map(tag => (
            <MenuItem key={tag.id} value={tag.id}>
              {tag.name}
            </MenuItem>
          ))}
        </TextField>

        <div className={classes.drawerFooter}>
          <Button onClick={handleClearFilters}>Limpar</Button>
          <Box display="flex" gridGap={8}>
            <Button onClick={() => setHistoryFiltersOpen(false)}>Cancelar</Button>
            <Button color="primary" variant="contained" onClick={handleApplyFilters}>
              Aplicar
            </Button>
          </Box>
        </div>
      </Drawer>

      <MainHeader>
        <Title>Atendimentos</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchHistory();
              fetchManagementSummary();
            }}
          >
            Atualizar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.pagePaper} variant="outlined">
        <div className={classes.introRow}>
          <div className={classes.introText}>
            <Typography variant="h5" className={classes.pageTitle}>
              Histórico de atendimentos
            </Typography>
            <Typography variant="body2" className={classes.pageSubtitle}>
              Consulte atendimentos encerrados com filtros administrativos e,
              quando necessário, use a aba de gerenciamento para ações em lote.
            </Typography>
          </div>
        </div>

        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabs}
        >
          <Tab value="history" icon={<HistoryOutlinedIcon />} label="Histórico" />
          <Tab
            value="management"
            icon={<AssignmentTurnedInOutlinedIcon />}
            label="Gerenciamento"
          />
        </Tabs>

        {activeTab === "history" ? (
          <>
            <div className={classes.actionRow}>
              <Typography variant="body2" className={classes.filterSummary}>
                {activeFiltersCount
                  ? `${activeFiltersCount} filtro(s) ativo(s)`
                  : "Sem filtros ativos. Exibindo os atendimentos encerrados mais recentes."}
              </Typography>
              <div className={classes.historyActions}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FilterListIcon />}
                  className={classes.actionButton}
                  onClick={() => setHistoryFiltersOpen(true)}
                >
                  Exibir filtros
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<GetAppIcon />}
                  className={classes.actionButton}
                  onClick={() => exportRowsToExcel(historyData.tickets, usersById)}
                  disabled={!historyData.tickets.length}
                >
                  Exportar
                </Button>
              </div>
            </div>

            <Grid container spacing={2} className={classes.metricsGrid}>
              <Grid item xs={12} md={3}>
                {renderMetricCard(
                  "Encerrados carregados",
                  historyData.tickets.length,
                  <AssignmentTurnedInOutlinedIcon />
                )}
              </Grid>
              <Grid item xs={12} md={3}>
                {renderMetricCard(
                  "Total encontrado",
                  historyData.count,
                  <HistoryOutlinedIcon />
                )}
              </Grid>
              <Grid item xs={12} md={3}>
                {renderMetricCard(
                  "Com atendente",
                  historyData.tickets.filter(
                    ticket => getAttendantLabel(ticket, usersById) !== "Sem atendente"
                  ).length,
                  <PersonOutlineOutlinedIcon />
                )}
              </Grid>
              <Grid item xs={12} md={3}>
                {renderMetricCard(
                  "No período",
                  historyFilters.dateFrom || historyFilters.dateTo
                    ? historyData.tickets.length
                    : "-",
                  <CalendarTodayOutlinedIcon />
                )}
              </Grid>
            </Grid>

            <Paper className={classes.tablePaper}>
              {loadingHistory ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight={260}
                >
                  <CircularProgress />
                </Box>
              ) : historyData.tickets.length ? (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.tableHeaderCell}>
                            Nome
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell}>
                            Conexão
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell}>
                            Departamento
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell}>
                            Atendente
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell}>
                            Assunto
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell}>
                            Encerrado em
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell}>
                            Número de protocolo
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {historyData.tickets.map(ticket => (
                          <TableRow hover key={ticket.id}>
                            <TableCell>
                              <Typography className={classes.rowName}>
                                {ticket.contact?.name || "Sem contato"}
                              </Typography>
                              <Typography className={classes.rowMeta}>
                                {ticket.contact?.number || "Número não disponível"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={ticket.whatsapp?.name || "Sem conexão"}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={ticket.queue?.name || "Sem fila"}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={getAttendantLabel(ticket, usersById)}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography>
                                {ticket.subject || "-"}
                              </Typography>
                              {ticket.resolutionSummary ? (
                                <Typography className={classes.rowMeta}>
                                  {ticket.resolutionSummary}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell>
                              <Typography>
                                {formatDateTime(getClosedDisplayDate(ticket))}
                              </Typography>
                              <Typography className={classes.rowMeta}>
                                Início: {formatDateTime(ticket.createdAt)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Button
                                color="primary"
                                endIcon={<OpenInNewIcon />}
                                className={classes.protocolButton}
                                onClick={() => openTicket(ticket.id)}
                              >
                                {formatTicketProtocol(ticket.id)}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={historyData.count}
                    rowsPerPage={40}
                    rowsPerPageOptions={[40]}
                    page={historyPage}
                    onPageChange={(_, page) => setHistoryPage(page)}
                  />
                </>
              ) : (
                <Paper variant="outlined" className={classes.emptyState}>
                  Nenhum atendimento encerrado encontrado com os filtros atuais.
                </Paper>
              )}
            </Paper>
          </>
        ) : (
          <>
            {loadingManagement ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={240}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Grid container spacing={2} className={classes.metricsGrid}>
                  <Grid item xs={12} md={3}>
                    {renderMetricCard(
                      "Conexões",
                      totals.connections || 0,
                      <ForumOutlinedIcon />
                    )}
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {renderMetricCard(
                      "Atendendo",
                      totals.open || 0,
                      <AssignmentTurnedInOutlinedIcon />
                    )}
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {renderMetricCard(
                      "Aguardando",
                      totals.pending || 0,
                      <ScheduleOutlinedIcon />
                    )}
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {renderMetricCard(
                      "Resolvidos",
                      totals.closed || 0,
                      <AssignmentTurnedInOutlinedIcon />
                    )}
                  </Grid>
                </Grid>

                <Paper variant="outlined" className={classes.actionsSection}>
                  <div className={classes.actionsHeader}>
                    <div>
                      <Typography className={classes.actionsTitle}>
                        Ações globais
                      </Typography>
                      <Typography
                        variant="body2"
                        className={classes.actionsDescription}
                      >
                        Use estas ações com cautela. Elas afetam atendimentos de
                        todas as conexões visíveis nesta tela.
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.actionsRow}>
                    <Button
                      className={`${classes.actionButton} ${classes.subtleButton}`}
                      variant="outlined"
                      color="primary"
                      onClick={() =>
                        handleRequestClose({
                          closeOpen: true,
                          closePending: false
                        })
                      }
                    >
                      Fechar "Atendendo"
                    </Button>
                    <Button
                      className={`${classes.actionButton} ${classes.subtleButton}`}
                      variant="outlined"
                      color="primary"
                      onClick={() =>
                        handleRequestClose({
                          closeOpen: false,
                          closePending: true
                        })
                      }
                    >
                      Fechar "Aguardando"
                    </Button>
                    <Button
                      className={`${classes.actionButton} ${classes.subtleButton}`}
                      variant="outlined"
                      color="secondary"
                      onClick={() =>
                        handleRequestClose({
                          closeOpen: true,
                          closePending: true
                        })
                      }
                    >
                      Fechar tudo
                    </Button>
                  </div>
                </Paper>

                {summary.items?.length ? (
                  <Grid container spacing={2}>
                    {summary.items.map(item => (
                      <Grid item xs={12} md={6} lg={4} key={item.id}>
                        <Paper variant="outlined" className={classes.connectionCard}>
                          <div className={classes.connectionHeader}>
                            <div>
                              <Typography className={classes.connectionTitle}>
                                {item.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                className={classes.connectionSubtitle}
                              >
                                Status da conexão: {item.status}
                              </Typography>
                            </div>
                            <Chip
                              size="small"
                              variant={
                                item.allowGroupMessages ? "default" : "outlined"
                              }
                              className={
                                item.allowGroupMessages
                                  ? classes.counterChip
                                  : undefined
                              }
                              label={
                                item.allowGroupMessages
                                  ? "Grupos ativos"
                                  : "Grupos desativados"
                              }
                            />
                          </div>

                          <Divider />

                          <div className={classes.countersRow}>
                            <Chip
                              className={classes.counterChip}
                              label={`Atendendo: ${item.openCount}`}
                            />
                            <Chip
                              className={classes.counterChip}
                              label={`Aguardando: ${item.pendingCount}`}
                            />
                            <Chip
                              className={classes.counterChip}
                              label={`Resolvidos: ${item.closedCount}`}
                            />
                          </div>

                          <div className={classes.connectionActions}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              className={classes.subtleButton}
                              onClick={() =>
                                handleRequestClose({
                                  whatsappId: item.id,
                                  name: item.name,
                                  closeOpen: true,
                                  closePending: false
                                })
                              }
                            >
                              Fechar "Atendendo"
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              className={classes.subtleButton}
                              onClick={() =>
                                handleRequestClose({
                                  whatsappId: item.id,
                                  name: item.name,
                                  closeOpen: false,
                                  closePending: true
                                })
                              }
                            >
                              Fechar "Aguardando"
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              className={classes.subtleButton}
                              onClick={() =>
                                handleRequestClose({
                                  whatsappId: item.id,
                                  name: item.name,
                                  closeOpen: true,
                                  closePending: true
                                })
                              }
                            >
                              Fechar todos
                            </Button>
                          </div>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Paper variant="outlined" className={classes.emptyState}>
                    Nenhuma conexão encontrada para gerenciamento de atendimentos.
                  </Paper>
                )}
              </>
            )}
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Attendances;
